import React from 'react';
import { FormContext } from 'react-hook-form';
import { KeyboardAvoidingView } from 'react-native';
import { ProgressBar } from 'react-native-paper';
import { t, Trans } from '@lingui/macro';

import Container from '@src/components/Container';
import TextInput from '@src/components/TextInput';
import VisibleStatusBar from '@src/components/VisibleStatusBar';
import { padding } from '@src/configuration/constants';
import { Gender } from '@src/enums';
import useAuthenticationTokens from '@src/hooks/useAuthenticationTokens';
import { PickedMedia } from '@src/hooks/useMediaPicker';
import useUserProfile from '@src/hooks/useUserProfile';
import { i18n } from '@src/i18n';

import AvatarPicker from './AvatarPicker';
import GenderPicker from './GenderPicker';
import { ButtonContainer, SubmitButton } from './styled';
import { Props } from './types';
import useEditProfile from './useEditProfile';

const Screen: React.FC<Props> = ({ route }) => {
  const {
    tokens: { userID },
  } = useAuthenticationTokens();
  const id = React.useMemo(() => {
    return route.params?.id || userID || '';
  }, [route.params, userID]);
  const userProfile = useUserProfile(id);
  const [media, setMedia] = React.useState<PickedMedia>();
  const { form, loading, handleSubmit, uploadProgress } = useEditProfile(media);

  const progress = parseInt(uploadProgress || '0', 10) / 100;
  const uploadInProgress = progress > 0 && progress < 1;

  return (
    <>
      <VisibleStatusBar />
      <Container style={{ paddingHorizontal: padding / 2 }}>
        {uploadInProgress && <ProgressBar progress={progress} />}
        <KeyboardAvoidingView>
          <FormContext {...form}>
            <AvatarPicker
              name="profilePicture"
              source={userProfile.data?.avatar}
              onImagePicked={setMedia}
            />
            <TextInput
              name="name"
              autoCapitalize="none"
              label={i18n._(t`Name`)}
              disabled={loading}
              defaultValue={userProfile.data?.name}
            />
            <TextInput
              name="username"
              autoCapitalize="none"
              label={i18n._(t`Username`)}
              disabled={loading}
              defaultValue={userProfile.data?.username}
            />
            <TextInput
              name="bio"
              label={i18n._(t`Bio`)}
              multiline
              numberOfLines={3}
              disabled={loading}
              defaultValue={userProfile?.data?.bio}
            />
            <GenderPicker
              name="gender"
              label={i18n._(t`Gender`)}
              selectedGender={userProfile.data?.gender as Gender}
              disabled={loading}
            />
            <ButtonContainer>
              <SubmitButton
                loading={loading || uploadInProgress}
                disabled={loading || uploadInProgress}
                onPress={handleSubmit}
                mode="contained"
              >
                <Trans>Submit</Trans>
              </SubmitButton>
            </ButtonContainer>
          </FormContext>
        </KeyboardAvoidingView>
      </Container>
    </>
  );
};

Screen.displayName = 'ProfileEdit';

export default React.memo(Screen);
